import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { flushAdminDatabase, getAdminDebugSnapshot } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Shield, RefreshCw, Wifi, Activity, Cpu, AlertTriangle, Database } from 'lucide-react'
import type { DebugProcessInfo, DebugSnapshot, DebugWsExchangeHealth } from '@/types'

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const parts: string[] = []
  if (d > 0) parts.push(`${d}d`)
  if (h > 0) parts.push(`${h}h`)
  if (m > 0) parts.push(`${m}m`)
  parts.push(`${s}s`)
  return parts.join(' ')
}

function SystemOverview({ snapshot }: { snapshot: DebugSnapshot }) {
  const sys = snapshot.system

  if (sys.error) {
    return (
      <Card>
        <CardContent className="py-4 text-sm text-destructive">{sys.error}</CardContent>
      </Card>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Cpu className="h-4 w-4" /> Process Info
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm font-mono">
          <div className="flex justify-between">
            <span className="text-muted-foreground">PID</span>
            <span>{sys.pid}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Uptime</span>
            <span>{formatUptime(sys.uptime_seconds)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Memory (RSS)</span>
            <span>{sys.memory_rss_mb} MB</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Memory (VMS)</span>
            <span>{sys.memory_vms_mb} MB</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">CPU</span>
            <span>{sys.cpu_percent}%</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Threads</span>
            <span>{sys.threads}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Service Health</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">WebSocket orchestrator</span>
            {'status' in snapshot.ws_health && snapshot.ws_health.status === 'not_initialized' ? (
              <Badge variant="secondary">Not initialized</Badge>
            ) : (
              <Badge>Active</Badge>
            )}
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Alert workers</span>
            {snapshot.alert_workers.error ? (
              <Badge variant="destructive">Error</Badge>
            ) : snapshot.alert_workers.status === 'not_initialized' ? (
              <Badge variant="secondary">Not initialized</Badge>
            ) : (
              <Badge>Active</Badge>
            )}
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total restarts</span>
            <span className="font-mono">{snapshot.alert_workers.total_restarts ?? 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Worker count</span>
            <span className="font-mono">{snapshot.alert_workers.num_workers ?? 0}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function ProcessWorkers({ snapshot }: { snapshot: DebugSnapshot }) {
  const processes = snapshot.processes ?? []
  const healthyCount = processes.filter((p) => p.healthy).length

  const typeLabel = (t: DebugProcessInfo['type']) => {
    switch (t) {
      case 'thread':
        return 'Thread'
      case 'asyncio_task':
        return 'Async Task'
      case 'process':
        return 'Process'
      default:
        return t
    }
  }

  if (processes.length === 0) {
    return <p className="text-sm text-muted-foreground p-4">No process data available.</p>
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-mono font-semibold">{processes.length}</div>
            <div className="text-xs text-muted-foreground mt-1">Total Processes</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-mono font-semibold">
              <Badge variant={healthyCount === processes.length ? 'default' : 'destructive'}>
                {healthyCount} / {processes.length}
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground mt-1">Healthy</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-mono font-semibold">
              {formatUptime(snapshot.alert_workers.uptime_seconds ?? snapshot.system.uptime_seconds)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">System Uptime</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-mono font-semibold">{snapshot.alert_workers.total_restarts ?? 0}</div>
            <div className="text-xs text-muted-foreground mt-1">Alert Restarts</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">All Processes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground text-xs uppercase tracking-wider">
                  <th className="py-2 px-3">Name</th>
                  <th className="py-2 px-3">Type</th>
                  <th className="py-2 px-3">PID</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">Details</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {processes.map((proc, idx) => (
                  <tr key={`${proc.name}-${idx}`} className="border-b last:border-0">
                    <td className="py-2 px-3 font-semibold">{proc.name}</td>
                    <td className="py-2 px-3 text-muted-foreground text-xs">{typeLabel(proc.type)}</td>
                    <td className="py-2 px-3">{proc.pid ?? '—'}</td>
                    <td className="py-2 px-3">
                      <Badge variant={proc.healthy ? 'default' : 'destructive'} className="text-xs">
                        {proc.status}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">
                      {proc.details && Object.keys(proc.details).length > 0
                        ? Object.entries(proc.details)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(' · ')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function WsSubscriptions({ snapshot }: { snapshot: DebugSnapshot }) {
  const wsHealth = snapshot.ws_health
  const wsStats = snapshot.ws_stats

  if ('status' in wsHealth && wsHealth.status === 'not_initialized') {
    return <p className="text-sm text-muted-foreground p-4">WebSocket orchestrator not initialized.</p>
  }

  const exchangeRows = Object.entries(wsHealth as Record<string, DebugWsExchangeHealth>)

  return (
    <div className="space-y-4">
      {wsStats && !wsStats.error && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Handlers', value: wsStats.total_handlers },
            { label: 'Connected', value: wsStats.connected_handlers },
            { label: 'Subscriptions', value: wsStats.total_subscriptions.toLocaleString() },
            { label: 'Messages', value: wsStats.total_messages.toLocaleString() },
          ].map((stat) => (
            <Card key={stat.label}>
              <CardContent className="pt-4 pb-3 text-center">
                <div className="text-2xl font-mono font-semibold">{stat.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {'error' in wsHealth ? (
        <p className="text-sm text-destructive">{String(wsHealth.error)}</p>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Exchange Connections</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground text-xs uppercase tracking-wider">
                    <th className="py-2 px-3">Exchange</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3 text-right">Subs</th>
                    <th className="py-2 px-3 text-right">Messages</th>
                    <th className="py-2 px-3 text-right">Errors</th>
                    <th className="py-2 px-3">Circuit Breaker</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {exchangeRows.map(([exchange, health]) => (
                    <tr key={exchange} className="border-b last:border-0">
                      <td className="py-2 px-3 font-semibold capitalize">{exchange}</td>
                      <td className="py-2 px-3">
                        <Badge variant={health.connected ? 'default' : 'destructive'} className="text-xs">
                          {health.connected ? 'Connected' : 'Disconnected'}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 text-right">{String(health.subscriptions ?? '—')}</td>
                      <td className="py-2 px-3 text-right">{String(health.messages_received ?? '—')}</td>
                      <td className="py-2 px-3 text-right">{String(health.errors ?? 0)}</td>
                      <td className="py-2 px-3">
                        {health.circuit_breaker_open ? (
                          <Badge variant="destructive" className="text-xs">
                            OPEN
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">Closed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default function AdminDebug() {
  const { isAdmin } = useAuth()
  const [flushStatus, setFlushStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const statusQuery = useQuery({
    queryKey: ['admin-debug-status'],
    queryFn: getAdminDebugSnapshot,
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  })
  const flushDbMutation = useMutation({
    mutationFn: flushAdminDatabase,
    onSuccess: (result) => {
      if (!result?.ok) {
        setFlushStatus({ type: 'error', message: result?.error || 'Failed to flush database' })
        return
      }
      const flushedCount = result.flushed_tables?.length || 0
      setFlushStatus({
        type: 'success',
        message: `Database flushed successfully (${flushedCount} tables)`,
      })
      statusQuery.refetch()
    },
    onError: (error) => {
      setFlushStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to flush database',
      })
    },
  })

  const handleFlushDb = () => {
    const confirmed = window.confirm(
      'Flush all runtime database tables? This will permanently remove collected market and alert state data.'
    )
    if (!confirmed) return
    setFlushStatus(null)
    flushDbMutation.mutate()
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-semibold">Access Denied</h2>
        <p className="text-muted-foreground">This page is restricted to administrators.</p>
      </div>
    )
  }

  const snapshot = statusQuery.data

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">System Debug</h1>
            <p className="text-sm text-muted-foreground">Live system status - admin only</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="destructive"
            onClick={handleFlushDb}
            disabled={flushDbMutation.isPending}
            className="gap-2"
          >
            <Database className="h-4 w-4" />
            {flushDbMutation.isPending ? 'Flushing...' : 'Flush DB'}
          </Button>
          {snapshot && (
            <span className="text-xs text-muted-foreground font-mono">
              Updated: {new Date(snapshot.snapshot_at + 'Z').toLocaleString()}
            </span>
          )}
          <Button onClick={() => statusQuery.refetch()} disabled={statusQuery.isFetching} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${statusQuery.isFetching ? 'animate-spin' : ''}`} />
            {statusQuery.isFetching ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {flushStatus && (
        <Card className={flushStatus.type === 'error' ? 'border-destructive' : 'border-emerald-500/40'}>
          <CardContent className={`py-3 text-sm ${flushStatus.type === 'error' ? 'text-destructive' : 'text-emerald-500'}`}>
            {flushStatus.message}
          </CardContent>
        </Card>
      )}

      {statusQuery.isError && (
        <Card className="border-destructive">
          <CardContent className="py-3 text-sm text-destructive">
            Failed to load system status: {(statusQuery.error as Error).message}
          </CardContent>
        </Card>
      )}

      {!snapshot ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Loading Live Status</h3>
            <p className="text-sm text-muted-foreground">Collecting current system information...</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="overview" className="w-full">
          <TabsList>
            <TabsTrigger value="overview" className="gap-1.5">
              <Cpu className="h-3.5 w-3.5" /> Overview
            </TabsTrigger>
            <TabsTrigger value="workers" className="gap-1.5">
              <Activity className="h-3.5 w-3.5" /> Workers
            </TabsTrigger>
            <TabsTrigger value="websockets" className="gap-1.5">
              <Wifi className="h-3.5 w-3.5" /> WebSockets
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <SystemOverview snapshot={snapshot} />
          </TabsContent>

          <TabsContent value="workers">
            <ProcessWorkers snapshot={snapshot} />
          </TabsContent>

          <TabsContent value="websockets">
            <WsSubscriptions snapshot={snapshot} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}
